import { useState, useEffect } from 'react';

// Simple mobile detection hook
export const useMobileDetection = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isDesktop, setIsDesktop] = useState(true);
  const [screenWidth, setScreenWidth] = useState(1200);
  const [screenHeight, setScreenHeight] = useState(800);
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>('landscape');
  const [deviceType, setDeviceType] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    const detectDevice = () => {
      if (typeof window === 'undefined') return;

      const width = window.innerWidth;
      const height = window.innerHeight;
      
      // Check if device supports touch
      const touchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      setIsTouchDevice(touchDevice);
      
      // Determine device type based on screen width
      let newDeviceType: 'mobile' | 'tablet' | 'desktop' = 'desktop';
      let newIsMobile = false;
      let newIsTablet = false;
      let newIsDesktop = true;

      if (width <= 768) {
        newDeviceType = 'mobile';
        newIsMobile = true;
        newIsTablet = false;
        newIsDesktop = false;
      } else if (width <= 1024) {
        newDeviceType = 'tablet';
        newIsMobile = false;
        newIsTablet = true;
        newIsDesktop = false;
      } else {
        newDeviceType = 'desktop';
        newIsMobile = false;
        newIsTablet = false;
        newIsDesktop = true;
      }

      // Determine orientation
      const newOrientation: 'portrait' | 'landscape' = width > height ? 'landscape' : 'portrait';

      // Update all states
      setIsMobile(newIsMobile);
      setIsTablet(newIsTablet);
      setIsDesktop(newIsDesktop);
      setScreenWidth(width);
      setScreenHeight(height);
      setOrientation(newOrientation);
      setDeviceType(newDeviceType);
    };

    // Initial detection
    detectDevice();

    // Add event listeners for resize and orientation change
    window.addEventListener('resize', detectDevice);
    window.addEventListener('orientationchange', detectDevice);

    // Cleanup
    return () => {
      window.removeEventListener('resize', detectDevice);
      window.removeEventListener('orientationchange', detectDevice);
    };
  }, []);

  return {
    isMobile,
    isTablet,
    isDesktop,
    screenWidth,
    screenHeight,
    orientation,
    deviceType,
    isTouchDevice,
  };
};

// Utility function to get responsive class names
export const getResponsiveClasses = (
  mobile: string,
  tablet: string = mobile,
  desktop: string = tablet
): string => {
  return `${mobile} md:${tablet} lg:${desktop}`;
};

// Utility function to get mobile-specific classes
export const getMobileClasses = (mobile: string, desktop: string = ''): string => {
  return `${mobile} lg:${desktop}`;
};

// Utility function to get desktop-specific classes
export const getDesktopClasses = (desktop: string, mobile: string = ''): string => {
  return `${mobile} lg:${desktop}`;
};
